import { campaignApiGet, campaignApiPatch, campaignApiPost } from '@/lib/api/campaign-api-client';
import type {
  OptInApiEnvelope,
  OptInSettingsResponseApi,
  PendingOptInsResponseApi,
  ResendOptInRequestApi,
  ResendOptInResponseApi,
  UpdateOptInSettingsRequestApi,
} from '../types/opt-in-settings-api.types';
import type {
  GetPendingOptInsQuery,
  OptInSettings,
  PendingOptInsResult,
  ResendOptInInput,
  ResendOptInResult,
  UpdateOptInSettingsInput,
} from '../types/opt-in-settings.types';

const DEFAULT_OPT_IN_SETTINGS: OptInSettings = {
  emailDoubleOptInEnabled: false,
  smsDoubleOptInEnabled: false,
  tokenTtlHours: 72,
  resendCooldownMinutes: 15,
  confirmedRedirectUrl: null,
};

const assertOptInApiSuccess = <T>(
  response: OptInApiEnvelope<T> | T | null | undefined,
  errorLabel: string
): T => {
  if (!response || typeof response !== 'object') {
    throw new Error(`${errorLabel}: invalid response structure`);
  }

  const envelope = response as OptInApiEnvelope<T>;

  if (envelope.success === false) {
    const message =
      envelope.message ||
      (Array.isArray(envelope.errors) ? envelope.errors.join(', ') : undefined) ||
      errorLabel;
    throw new Error(message);
  }

  if (envelope.data !== undefined && envelope.data !== null) {
    return envelope.data;
  }

  if (!('data' in envelope)) {
    return response as T;
  }

  throw new Error(`${errorLabel}: missing data payload`);
};

const mapOptInSettings = (data: OptInSettingsResponseApi): OptInSettings => ({
  emailDoubleOptInEnabled: Boolean(data.emailDoubleOptInEnabled),
  smsDoubleOptInEnabled: Boolean(data.smsDoubleOptInEnabled),
  tokenTtlHours: data.tokenTtlHours ?? DEFAULT_OPT_IN_SETTINGS.tokenTtlHours,
  resendCooldownMinutes:
    data.resendCooldownMinutes ?? DEFAULT_OPT_IN_SETTINGS.resendCooldownMinutes,
  confirmedRedirectUrl: data.confirmedRedirectUrl?.trim() || null,
});

export const getOptInSettings = async (): Promise<OptInSettings> => {
  const response = await campaignApiGet<OptInApiEnvelope<OptInSettingsResponseApi>>(
    '/v1/settings/optin'
  );
  const data = assertOptInApiSuccess(response, 'Failed to fetch double opt-in settings');
  return mapOptInSettings(data);
};

export const updateOptInSettings = async (
  input: UpdateOptInSettingsInput
): Promise<OptInSettings> => {
  const payload: UpdateOptInSettingsRequestApi = {};

  if (input.emailDoubleOptInEnabled !== undefined) {
    payload.emailDoubleOptInEnabled = input.emailDoubleOptInEnabled;
  }
  if (input.tokenTtlHours !== undefined) {
    payload.tokenTtlHours = input.tokenTtlHours;
  }
  if (input.resendCooldownMinutes !== undefined) {
    payload.resendCooldownMinutes = input.resendCooldownMinutes;
  }
  if (input.confirmedRedirectUrl !== undefined) {
    payload.confirmedRedirectUrl = input.confirmedRedirectUrl;
  }

  const response = await campaignApiPatch<OptInApiEnvelope<OptInSettingsResponseApi>>(
    '/v1/settings/optin',
    payload
  );
  const data = assertOptInApiSuccess(response, 'Failed to update double opt-in settings');
  return mapOptInSettings(data);
};

export const getPendingOptIns = async (
  query: GetPendingOptInsQuery = {}
): Promise<PendingOptInsResult> => {
  const limit = query.limit ?? 50;
  const offset = query.offset ?? 0;

  const response = await campaignApiGet<OptInApiEnvelope<PendingOptInsResponseApi>>(
    '/v1/settings/optin/pending',
    { limit, offset }
  );
  const data = assertOptInApiSuccess(response, 'Failed to fetch pending confirmations');

  const rawItems = Array.isArray(data) ? data : ((data as PendingOptInsResponseApi).items ?? []);
  const items = rawItems.map((item: any) => ({
    contactId: String(item.contactId || item.id || item.itemId || ''),
    channel: String(item.channel || 'Email'),
    email: String(item.email || ''),
    sentAt: String(item.sentAt || ''),
    expiresAt: String(item.expiresAt || ''),
    resendCount: Number(item.resendCount ?? 0),
  }));

  return {
    items,
    pageInfo: (data as any).pageInfo ?? {
      limit,
      offset,
      total: items.length,
      count: items.length,
      hasNext: false,
      hasPrev: false,
    },
  };
};

export const resendOptIn = async ({
  contactId,
  idempotencyKey,
  channel = 'email',
}: ResendOptInInput): Promise<ResendOptInResult> => {
  const body: ResendOptInRequestApi = { channel };
  const headers = {
    'Idempotency-Key': idempotencyKey,
  };

  const response = await campaignApiPost<OptInApiEnvelope<ResendOptInResponseApi>>(
    `/v1/contacts/${contactId}/optin/resend`,
    body,
    headers
  );
  const data = assertOptInApiSuccess(response, 'Failed to resend confirmation');
  return data;
};
